import React, { useState } from "react";
import { Mail, Lock, ExternalLink, ArrowUpRight } from "lucide-react";
import { sendMagicLink } from "@/lib/api";
import { usePrice } from "@/lib/price";

const WHOP_FALLBACK = "https://whop.com/checkout/plan_hfsvR7lsOjQds";

/**
 * LoginPanel — the magic-link signup form. Always returns ok:true (no
 * account enumeration); we just show a success state and let the inbox do
 * the rest. Price + checkout URL come from /api/price.
 */
export default function LoginPanel({ source = "login", compact = false }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const { price, checkout } = usePrice();
  const whop = checkout || WHOP_FALLBACK;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      setErr("That doesn't look like an email.");
      return;
    }
    setErr("");
    setSubmitting(true);
    try {
      await sendMagicLink(email);
      // Public CORS endpoint always returns ok:true (no enumeration).
      // Even on network failure we show "check your email" — they can retry.
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="login-panel"
      className={`bg-white border-4 border-[#0a0a0a] shadow-brutal-lg ${
        compact ? "p-6" : "p-7 md:p-9"
      }`}
    >
      <div className="font-accent text-xs bg-[#0a0a0a] text-[#FFC400] inline-block px-2.5 py-1 mb-4">
        FREE ACCOUNT
      </div>
      <h1
        data-testid="login-panel-title"
        className={`font-display leading-[0.95] ${
          compact ? "text-3xl" : "text-4xl md:text-5xl"
        }`}
      >
        {sent ? (
          <>
            Check your <span className="bg-[#00FF66] px-2">inbox.</span>
          </>
        ) : (
          <>
            Log in or <span className="bg-[#FFC400] px-2">sign up.</span>
          </>
        )}
      </h1>

      {!sent ? (
        <>
          <p className="font-mono-body text-sm text-[#0a0a0a]/75 mt-4">
            We&apos;ll email you a one-time link. No password to remember.
          </p>
          <form onSubmit={submit} className="mt-6 space-y-3">
            <label htmlFor="login-email" className="font-accent text-xs block">
              EMAIL
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              required
              placeholder="you@inbox.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="w-full bg-[#FDFBF7] border-4 border-[#0a0a0a] px-4 py-3 font-mono-body text-base outline-none focus:bg-white focus:translate-x-[-2px] focus:translate-y-[-2px] focus:shadow-brutal-sm transition"
            />
            {err && (
              <div
                data-testid="login-error"
                className="font-accent text-xs text-[#E10600] bg-[#E10600]/10 border-2 border-[#E10600] px-3 py-2"
              >
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              data-source={source}
              className="hover-pop w-full inline-flex items-center justify-center gap-2 bg-[#E10600] text-white border-4 border-[#0a0a0a] px-5 py-4 font-accent text-base shadow-brutal disabled:opacity-60 disabled:cursor-wait"
            >
              {submitting ? "Sending…" : "Send me the link"}
              <Mail size={16} strokeWidth={3} />
            </button>
          </form>
        </>
      ) : (
        <p
          data-testid="login-sent"
          className="font-mono-body text-sm text-[#0a0a0a]/80 mt-4 leading-relaxed"
        >
          We just sent a sign-in link to{" "}
          <strong className="bg-[#FFC400] px-1">{email}</strong>. Click it from any
          device — you&apos;ll land in your dashboard automatically.
        </p>
      )}

      <div className="mt-7 pt-5 border-t-2 border-dashed border-[#0a0a0a]/30">
        <div className="font-mono-body text-xs text-[#0a0a0a]/70 mb-3">
          Want every tool? Skip the free tier:
        </div>
        <a
          href={whop}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="login-lock"
          className="hover-pop w-full inline-flex items-center justify-center gap-2 bg-[#FFC400] text-[#0a0a0a] border-4 border-[#0a0a0a] px-5 py-3.5 font-accent text-base shadow-brutal-sm"
        >
          <Lock size={15} strokeWidth={3} /> Lock {price} — lifetime
          <ExternalLink size={13} strokeWidth={3} />
        </a>
      </div>

      <p className="font-mono-body text-[11px] text-[#0a0a0a]/50 mt-5">
        By continuing you agree to our{" "}
        <a href="/terms" className="border-b-2 border-[#0a0a0a]/40">
          Terms
        </a>{" "}
        &amp;{" "}
        <a href="/privacy" className="border-b-2 border-[#0a0a0a]/40">
          Privacy
        </a>
        . {price} one-time · 7-day refund via Whop.
      </p>

      {!compact && (
        <a
          href="/"
          className="mt-5 inline-flex items-center gap-2 font-mono-body text-xs text-[#0a0a0a]/50 hover:text-[#0a0a0a]"
        >
          <ArrowUpRight size={12} strokeWidth={3} /> Back to jobhacki.com
        </a>
      )}
    </div>
  );
}
